import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { Dialog, DialogContent, DialogFooter } from '../Dialog'
import { useEffect } from 'react'
import { grundList } from '../../models/BefehlGrund'
import { Checkbox, FormErrorMessage, Input } from '../form'
import Button from '../Button'
import { CancelIcon24, DoneIcon32 } from '../../icons'
import { FREITEXT_SHORT_MAX_LENGTH, FREITEXT_SHORT_REGEXP } from '../../utils'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { allowedChars } from '../../utils/customValidationMessage'

export interface GrundDialogData {
  gruende: string
  fieldName: string
}

export interface GrundDialogProps {
  dialogState: boolean
  onSubmit: (gruende: string, fieldName: string) => void
  closeDialog: () => void
  dialogData?: GrundDialogData
  'data-testid': string
}

const FormSchema = z.object({
  gruende: z.record(z.string(), z.boolean()),
  andererGrund: z
    .string()
    .max(FREITEXT_SHORT_MAX_LENGTH)
    .superRefine(allowedChars(FREITEXT_SHORT_REGEXP))
    .default('')
    .nullish()
})

type FormData = z.infer<typeof FormSchema>

export default function GrundDialog({
  dialogState,
  onSubmit,
  closeDialog,
  dialogData,
  'data-testid': testId
}: Readonly<GrundDialogProps>) {
  const { t } = useTranslation('ui-befehle')

  const formMethods = useForm<FormData>({
    defaultValues: {
      gruende: {},
      andererGrund: ''
    },
    criteriaMode: 'all',
    resolver: zodResolver(FormSchema)
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = formMethods

  const parseGruende = (gruende: string | undefined): FormData => {
    if (gruende) {
      const parsedGruende: Record<string, boolean> = {}
      let andererGrund = ''
      gruende.split('\n').map((row) => {
        if (row.startsWith('Grund Nr.')) {
          const grundNr = row.substring(10, 12)
          parsedGruende[grundNr] = true
        } else if (row.startsWith('Anderer Grund - ')) {
          andererGrund = row.substring(16, row.length)
        }
      })

      return {
        gruende: parsedGruende,
        andererGrund: andererGrund
      }
    }
    return {
      gruende: {},
      andererGrund: ''
    }
  }

  useEffect(() => {
    reset(parseGruende(dialogData?.gruende))
  }, [dialogState, dialogData, reset])

  const formatGruende = (data: FormData) => {
    const selectedGruende = Object.keys(data.gruende).filter(
      (key) => data.gruende[key]
    )
    const formattedGruende = selectedGruende.map((grundNr) => {
      const grund = grundList.find((g) => g.nr === grundNr)
      return `Grund Nr. ${grundNr} - ${grund?.anlass}`
    })
    if (data.andererGrund) {
      const formattedAndererGrund = `Anderer Grund - ${data.andererGrund}`
      return formattedGruende.concat(formattedAndererGrund).join('\n')
    }
    return formattedGruende.join('\n')
  }

  const onFormSubmit = (data: FormData) => {
    onSubmit(formatGruende(data), dialogData?.fieldName ?? '')
    closeDialog()
  }

  return (
    <Dialog
      state={dialogState}
      title={t('grund_dialog.title')}
      onClose={closeDialog}
      data-testid={testId}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent className="mt-2" data-testid={`${testId}-content`}>
            <FormErrorMessage
              errors={errors}
              name="gruende"
              data-testid={`${testId}-error`}
            />
            {grundList.map((grund) => (
              <div
                key={grund.nr}
                className="grid grid-cols-[0.125fr,0.125fr,1.5fr,0.5fr] mb-0.25"
              >
                <Checkbox
                  id={`grund-${grund.nr}`}
                  label=""
                  className="border-b border-cool-gray-500 pr-0.25 pb-0.25"
                  {...register(`gruende.${grund.nr}`)}
                  data-testid={`${testId}-grund-${grund.nr}-checkbox`}
                />
                <span
                  className="min-w-2 font-semibold pr-0.5 border-b border-cool-gray-500"
                  data-testid={`${testId}-grund-${grund.nr}`}
                >
                  {grund.nr}
                </span>
                <span
                  className="border-b border-cool-gray-500"
                  data-testid={`${testId}-grund-${grund.nr}-anlass`}
                >
                  {grund.anlass}
                </span>
                <span
                  className="font-semibold text-right pr-0.25 border-b border-cool-gray-500 bg-cool-gray-100"
                  data-testid={`${testId}-grund-${grund.nr}-kmh`}
                >
                  {grund.kmh}
                </span>
              </div>
            ))}
            <span
              className="text-xs flex"
              data-testid={`${testId}-grund-anmerkung-1`}
            >
              {t('grund_dialog.anmerkung1')}
            </span>
            <span
              className="text-xs flex"
              data-testid={`${testId}-grund-anmerkung-2`}
            >
              {t('grund_dialog.anmerkung2')}
            </span>
            <span
              className="flex flex-col gap-1 m-1"
              data-testid={`${testId}-grund-anderer-grund-input`}
            >
              <Input
                type="text"
                label={t('grund_dialog.anderer_grund')}
                variant="solid"
                errors={errors}
                {...register('andererGrund')}
                data-testid={`${testId}-anderer-grund`}
              />
            </span>
          </DialogContent>
          <DialogFooter data-testid={`${testId}-footer`}>
            <div className="flex flex-col gap-1">
              <Button
                type="submit"
                variant="brand-primary"
                data-testid={`${testId}-submit-button`}
              >
                <DoneIcon32
                  className="shrink-0"
                  data-testid={`${testId}-apply-button-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-submit-button-text`}
                >
                  {t('grund_dialog.actions.submit')}
                </span>
              </Button>
              <Button
                type="button"
                variant="secondary-outline"
                onClick={closeDialog}
                data-testid={`${testId}-cancel-button`}
              >
                <CancelIcon24
                  className="shrink-0"
                  data-testid={`${testId}-cancel-button-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-cancel-button-text`}
                >
                  {t('grund_dialog.actions.cancel')}
                </span>
              </Button>
            </div>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
